import React, { useState, useEffect, useRef } from "react";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import axios from "axios";
import "./viewTestRecordsScreen.css";
import "./dashboard.css";
import Sidebar from "./Sidebar";
import {
  FaPaperPlane,
  FaPrint,
  FaSearch,
  FaFilter,
  FaSave,
  FaTimes,
  FaCalendarAlt,
  FaBook,
  FaClipboardCheck,
  FaArrowUp,
  FaArrowDown,
  FaSpinner,
  FaExclamationTriangle,
  FaDatabase,
  FaCheckCircle,
  FaExclamationCircle,
  FaIdCard,
  FaSignature,
  FaEdit,
  FaUpload,
} from "react-icons/fa";

// PHP API Base URL
const PHP_API_BASE = "https://mhsdev.online/MHS-WEB/api/ManageTestSheets.php";

const UpdateTestSheet = () => {
  const { testId } = useParams();
  const navigate = useNavigate();
  const inputRefs = useRef([]);

  const [formData, setFormData] = useState({
    testNo: "",
    testName: "",
    testId: testId || "",
    subject: "",
    totalMarks: "",
    class: "",
    section: "",
    date: new Date().toISOString().split("T")[0],
    testDay: new Date().toLocaleString("en-us", { weekday: "long" }),
  });

  const [students, setStudents] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [sections, setSections] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentFocusIndex, setCurrentFocusIndex] = useState(-1);
  const [apiStatus, setApiStatus] = useState({
    subjects: false,
    sections: false,
    students: false,
    update: false,
  });

  // Function to handle API errors
  const handleApiError = (error, context = "operation") => {
    console.error(`API Error in ${context}:`, error);

    let errorMessage = error.message || "Unknown error occurred";
    if (error.response) {
      errorMessage =
        error.response.data?.error ||
        error.response.data?.message ||
        `HTTP ${error.response.status}`;
    }

    Swal.fire({
      icon: "error",
      title: "Server Error",
      text: `Failed to ${context}. ${errorMessage}`,
      confirmButtonColor: "#e53e3e",
      footer: `<small>Please check if the PHP server is running at ${PHP_API_BASE}</small>`,
    });
  };

  // Fetch test details by ID
  const fetchTestDetails = async () => {
    if (!testId) {
      Swal.fire({
        icon: "error",
        title: "Missing Test ID",
        text: "No test ID provided for update",
        confirmButtonText: "Go Back",
      }).then(() => {
        navigate("/test-records");
      });
      return;
    }

    setIsLoading(true);
    try {
      const response = await axios.get(PHP_API_BASE, {
        params: {
          action: "get-test-by-id",
          test_id: testId,
        },
      });

      if (
        response.data.success &&
        response.data.data &&
        response.data.data.length > 0
      ) {
        const testData = response.data.data[0];
        const summary = response.data.summary;

        // Set form data from the first record (all records have same test details)
        setFormData({
          testNo: testData.test_no || "",
          testName: testData.testName || testData.test_name || "",
          testId: testData.test_id || testId,
          subject: testData.subject || "",
          totalMarks: testData.total_marks || "",
          class: testData.class || "",
          section: testData.section || "",
          date: testData.test_date || new Date().toISOString().split("T")[0],
          testDay:
            testData.test_day ||
            new Date().toLocaleString("en-us", { weekday: "long" }),
        });

        // Set students from all records
        const studentsData = response.data.data.map((record, index) => ({
          id: record.student_id,
          name: record.student_name,
          father_name: record.father_name || "N/A",
          class: record.class,
          section: record.section,
          subject: record.subject,
          totalMarks: parseFloat(record.total_marks) || 0,
          obtainedMarks: parseFloat(record.obtained_marks) || 0,
          percentage: parseFloat(record.percentage) || 0,
          date: record.test_date,
          day: record.test_day,
          status:
            parseFloat(record.obtained_marks) > 0 ? "submitted" : "pending",
          mobile: record.mobile || "N/A",
          b_form_no: record.b_form_no || "N/A",
          address: record.address || "N/A",
          gender: record.gender || "N/A",
          family_status: record.family_status || "N/A",
        }));

        setStudents(studentsData);

        // Fetch subjects and sections for the class
        if (testData.class) {
          fetchSubjects(testData.class);
          fetchSections(testData.class);
        }
      } else {
        Swal.fire({
          icon: "error",
          title: "Test Not Found",
          text: `Test with ID ${testId} was not found`,
          confirmButtonText: "Go Back",
        }).then(() => {
          navigate("/test-records");
        });
      }
    } catch (error) {
      console.error("Failed to fetch test details:", error);
      Swal.fire({
        icon: "error",
        title: "Failed to Load Test",
        text: "Could not fetch test details. Please try again.",
        confirmButtonText: "Go Back",
      }).then(() => {
        navigate("/test-records");
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch subjects for the class
  const fetchSubjects = async (className) => {
    if (!className) {
      setSubjects([]);
      return;
    }

    try {
      setApiStatus((prev) => ({ ...prev, subjects: "loading" }));
      const response = await axios.get(PHP_API_BASE, {
        params: {
          action: "get-subjects",
          className: className,
        },
      });

      if (response.data.success) {
        setSubjects(response.data.data || []);
        setApiStatus((prev) => ({ ...prev, subjects: "success" }));
      } else {
        setSubjects([]);
        setApiStatus((prev) => ({ ...prev, subjects: "empty" }));
      }
    } catch (error) {
      console.error("Failed to fetch subjects:", error);
      setSubjects([]);
      setApiStatus((prev) => ({ ...prev, subjects: "error" }));
    }
  };

  // Fetch sections for the class
  const fetchSections = async (className) => {
    if (!className) {
      setSections([]);
      return;
    }

    try {
      setApiStatus((prev) => ({ ...prev, sections: "loading" }));
      const response = await axios.get(PHP_API_BASE, {
        params: {
          action: "get-class-sections",
          className: className,
        },
      });

      if (response.data.success) {
        const sectionsFromDB = response.data.data || [];
        const studentSections = response.data.student_sections || [];

        const allSections = [
          ...new Set([
            ...sectionsFromDB.map((s) => s.section_hint || s.section_name),
            ...studentSections,
          ]),
        ].filter(Boolean);

        setSections(
          allSections.map((section) => ({
            id: section,
            section_hint: section,
            section_name: section,
          }))
        );
        setApiStatus((prev) => ({ ...prev, sections: "success" }));
      } else {
        setSections([]);
        setApiStatus((prev) => ({ ...prev, sections: "empty" }));
      }
    } catch (error) {
      console.error("Failed to fetch sections:", error);
      setSections([]);
      setApiStatus((prev) => ({ ...prev, sections: "error" }));
    }
  };

  useEffect(() => {
    fetchTestDetails();
  }, [testId]);

  // Keyboard navigation effect
  useEffect(() => {
    const handleGlobalKeyDown = (e) => {
      if (
        ["ArrowUp", "ArrowDown", "Enter"].includes(e.key) &&
        document.activeElement.className === "marks-input"
      ) {
        e.preventDefault();
        const currentIndex = Array.from(
          document.querySelectorAll(".marks-input")
        ).indexOf(document.activeElement);

        if (
          e.key === "ArrowDown" &&
          currentIndex < inputRefs.current.length - 1
        ) {
          const nextInput = inputRefs.current[currentIndex + 1];
          if (nextInput) {
            nextInput.focus();
            nextInput.select();
            setCurrentFocusIndex(currentIndex + 1);
          }
        } else if (e.key === "ArrowUp" && currentIndex > 0) {
          const prevInput = inputRefs.current[currentIndex - 1];
          if (prevInput) {
            prevInput.focus();
            prevInput.select();
            setCurrentFocusIndex(currentIndex - 1);
          }
        } else if (e.key === "Enter") {
          if (currentIndex < inputRefs.current.length - 1) {
            const nextInput = inputRefs.current[currentIndex + 1];
            if (nextInput) {
              nextInput.focus();
              nextInput.select();
              setCurrentFocusIndex(currentIndex + 1);
            }
          } else {
            inputRefs.current[0]?.focus();
            inputRefs.current[0]?.select();
            setCurrentFocusIndex(0);
          }
        }
      }
    };

    document.addEventListener("keydown", handleGlobalKeyDown);
    return () => document.removeEventListener("keydown", handleGlobalKeyDown);
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleMarksUpdate = (e, studentId, index) => {
    const value = parseFloat(e.target.value) || 0;
    const totalMarks = parseFloat(formData.totalMarks) || 0;

    if (value > totalMarks) {
      Swal.fire({
        title: "Invalid Input",
        text: `Obtained marks cannot exceed ${totalMarks}.`,
        icon: "warning",
        confirmButtonText: "OK",
        customClass: { popup: "swal-popup" },
        showConfirmButton: true,
        timer: 1500,
      });

      if (inputRefs.current[index]) {
        inputRefs.current[index].focus();
        inputRefs.current[index].select();
      }
      return;
    }

    const updatedStudents = students.map((student) =>
      student.id === studentId
        ? {
            ...student,
            obtainedMarks: value,
            percentage:
              totalMarks > 0 ? ((value / totalMarks) * 100).toFixed(2) : 0,
            status: value > 0 ? "submitted" : "pending",
          }
        : student
    );
    setStudents(updatedStudents);
  };

  const handleBulkMarksUpdate = (marks) => {
    const totalMarks = parseFloat(formData.totalMarks) || 0;
    const updatedStudents = students.map((student) => ({
      ...student,
      obtainedMarks: marks,
      percentage: totalMarks > 0 ? ((marks / totalMarks) * 100).toFixed(2) : 0,
      status: marks > 0 ? "submitted" : "pending",
    }));
    setStudents(updatedStudents);
  };

  const handleUpdateTest = async () => {
    if (students.length === 0) {
      Swal.fire({
        title: "No Data",
        text: "No student data to update!",
        icon: "warning",
        confirmButtonText: "OK",
        customClass: { popup: "swal-popup" },
      });
      return;
    }

    const incompleteStudents = students.filter((s) => s.obtainedMarks === 0);
    if (incompleteStudents.length > 0) {
      const result = await Swal.fire({
        title: "Incomplete Data",
        html: `${incompleteStudents.length} student(s) have 0 marks. Continue anyway?`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Update Anyway",
        cancelButtonText: "Review",
        confirmButtonColor: "#00b09b",
        cancelButtonColor: "#6c757d",
        customClass: { popup: "swal-popup" },
      });
      if (!result.isConfirmed) return;
    }

    Swal.fire({
      title: "Update Test",
      html: `
        <div style="text-align: left;">
          <p>This will update all test marks in the database.</p>
          <p><strong>Test Details:</strong></p>
          <ul style="margin-left: 20px;">
            <li>Test ID: <strong>${formData.testId}</strong></li>
            <li>Test Name: ${formData.testName}</li>
            <li>Test Number: ${formData.testNo}</li>
            <li>Subject: ${formData.subject}</li>
            <li>Class: ${formData.class}</li>
            <li>Section: ${formData.section}</li>
            <li>Date: ${formData.date}</li>
            <li>Total Students: ${students.length}</li>
          </ul>
          <p>Continue with update?</p>
        </div>
      `,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, Update",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#6a11cb",
      cancelButtonColor: "#6c757d",
      customClass: { popup: "swal-popup" },
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          setIsUpdating(true);
          setApiStatus((prev) => ({ ...prev, update: "loading" }));

          const payload = {
            testNo: formData.testNo,
            testId: formData.testId,
            testName: formData.testName,
            subject: formData.subject,
            totalMarks: parseFloat(formData.totalMarks),
            class: formData.class,
            section: formData.section,
            date: formData.date,
            testDay: formData.testDay,
            testResults: students.map((student) => ({
              studentId: student.id,
              studentName: student.name,
              obtainedMarks: parseFloat(student.obtainedMarks),
              percentage: parseFloat(student.percentage),
            })),
          };

          console.log("Updating test:", payload);

          // First try the update endpoint, if it doesn't exist, use submit endpoint
          let endpoint = `${PHP_API_BASE}?action=update-testsheet`;
          let method = "PUT";

          try {
            // Try update endpoint
            const response = await fetch(endpoint, {
              method: "PUT",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify(payload),
            });

            const resultData = await response.json();

            if (resultData.success) {
              showSuccessMessage(resultData);
              setApiStatus((prev) => ({ ...prev, update: "success" }));
            } else {
              // If update endpoint fails, try delete and recreate
              await updateByDeleteAndRecreate(payload);
            }
          } catch (updateError) {
            console.log(
              "Update endpoint failed, trying delete and recreate:",
              updateError
            );
            await updateByDeleteAndRecreate(payload);
          }
        } catch (error) {
          console.error("Error updating test:", error);
          showErrorMessage(error);
          setApiStatus((prev) => ({ ...prev, update: "error" }));
        } finally {
          setIsUpdating(false);
        }
      }
    });
  };

  const updateByDeleteAndRecreate = async (payload) => {
    try {
      // First delete the existing test
      const deleteResponse = await fetch(
        `${PHP_API_BASE}?action=delete-test-record&test_id=${formData.testId}`,
        {
          method: "DELETE",
        }
      );

      const deleteResult = await deleteResponse.json();

      if (deleteResult.success) {
        // Then create a new test with the same ID
        const createResponse = await fetch(
          `${PHP_API_BASE}?action=submit-testsheet`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify(payload),
          }
        );

        const createResult = await createResponse.json();

        if (createResult.success) {
          showSuccessMessage(createResult);
          setApiStatus((prev) => ({ ...prev, update: "success" }));
        } else {
          throw new Error(createResult.error || "Failed to recreate test");
        }
      } else {
        throw new Error(deleteResult.error || "Failed to delete existing test");
      }
    } catch (error) {
      throw error;
    }
  };

  const showSuccessMessage = (resultData) => {
    Swal.fire({
      title: "Success!",
      html: `
        <div style="text-align: left;">
          <div style="background: #f0fff4; padding: 15px; border-radius: 8px; border-left: 4px solid #48bb78; margin-bottom: 15px;">
            <p style="color: #48bb78; font-weight: 600;">
              ✓ ${resultData.message || "Test updated successfully!"}
            </p>
          </div>
          <div style="background: #f7fafc; padding: 12px; border-radius: 6px;">
            <p style="margin: 0 0 8px 0; font-weight: 600;">Update Summary:</p>
            <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 8px;">
              <div style="color: #38a169;">
                <span style="font-weight: 600;">✓ Students:</span> ${
                  resultData.stats?.successful || students.length
                }
              </div>
              <div style="color: #4a5568;">
                <span style="font-weight: 600;">🆔 Test ID:</span> ${
                  formData.testId
                }
              </div>
              <div style="color: #4a5568;">
                <span style="font-weight: 600;">📅 Date:</span> ${formData.date}
              </div>
              <div style="color: #4a5568;">
                <span style="font-weight: 600;">📚 Subject:</span> ${
                  formData.subject
                }
              </div>
              <div style="color: #4a5568;">
                <span style="font-weight: 600;">🏫 Class:</span> ${
                  formData.class
                }
              </div>
              <div style="color: #4a5568;">
                <span style="font-weight: 600;">👥 Section:</span> ${
                  formData.section
                }
              </div>
            </div>
          </div>
        </div>
      `,
      icon: "success",
      confirmButtonText: "OK",
      confirmButtonColor: "#00b09b",
      customClass: { popup: "swal-popup" },
    });
  };

  const showErrorMessage = (error) => {
    let errorMessage = error.message;
    if (error.response?.data?.error) {
      errorMessage = error.response.data.error;
    }

    Swal.fire({
      title: "Update Failed",
      html: `
        <div style="text-align: left;">
          <p style="color: #e53e3e; font-weight: 600;">${errorMessage}</p>
          <div style="background: #fff5f5; padding: 12px; border-radius: 6px; margin-top: 1rem; border-left: 4px solid #e53e3e;">
            <p style="margin: 0 0 8px 0; font-weight: 600; color: #718096;">Troubleshooting Steps:</p>
            <ul style="margin-left: 20px; color: #718096;">
              <li>Check if PHP backend is running</li>
              <li>Verify database connection is working</li>
              <li>Ensure you have permission to update tests</li>
              <li>Try refreshing the page and trying again</li>
            </ul>
          </div>
        </div>
      `,
      icon: "error",
      confirmButtonText: "OK",
      customClass: { popup: "swal-popup" },
    });
  };

  const handleSendData = () => {
    navigate("/Print-Test-Sheet-Sample", {
      state: { students, formData, className: formData.class },
    });
  };

  const filteredStudents = students.filter(
    (student) =>
      student.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      student.id.toString().includes(searchTerm)
  );

  const calculateStatistics = () => {
    const totalStudents = students.length;
    const submittedStudents = students.filter(
      (s) => s.obtainedMarks > 0
    ).length;
    const averageMarks =
      students.reduce((sum, s) => sum + parseFloat(s.obtainedMarks), 0) /
        totalStudents || 0;
    const highestMarks = Math.max(...students.map((s) => s.obtainedMarks));

    return {
      totalStudents,
      submittedStudents,
      averageMarks: averageMarks.toFixed(2),
      highestMarks,
      completionRate:
        totalStudents > 0
          ? ((submittedStudents / totalStudents) * 100).toFixed(1)
          : 0,
    };
  };

  const stats = calculateStatistics();

  const getStatusColor = (status) => {
    switch (status) {
      case "success":
        return "#48bb78";
      case "loading":
        return "#4299e1";
      case "error":
        return "#e53e3e";
      case "empty":
        return "#d69e2e";
      default:
        return "#a0aec0";
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case "success":
        return "✓ Connected";
      case "loading":
        return "⏳ Loading...";
      case "error":
        return "✗ Error";
      case "empty":
        return "⚠ No Data";
      default:
        return "--";
    }
  };

  if (isLoading) {
    return (
      <div className="dashboard-wrapper">
        <Sidebar />
        <main className="main-content test-sheet-container">
          <div className="loading-overlay">
            <div className="loading-spinner">
              <FaSpinner className="spin" size={48} />
              <p>Loading test data...</p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="dashboard-wrapper">
      <Sidebar />
      <main className="main-content test-sheet-container">
        {/* Test Sheet Wrapper */}
        <div className="test-sheet-wrapper">
          {/* Header */}
          <div className="sheet-header">
            <div className="header-left">
              <h1 className="sheet-title">
                <span className="title-icon">
                  <FaEdit />
                </span>
                Update Test Sheet - {formData.class}
              </h1>
              <div className="sheet-info">
                <span className="info-badge">
                  <FaBook /> {formData.subject || "No Subject"}
                </span>
                <span className="info-badge">
                  🏫 {formData.section || "No Section"}
                </span>
                {formData.testName && (
                  <span className="info-badge test-name-badge">
                    📝 {formData.testName}
                  </span>
                )}
                {formData.testId && (
                  <span className="info-badge test-id-badge">
                    🆔 ID: {formData.testId}
                  </span>
                )}
                <span className="info-badge">
                  <FaCalendarAlt /> {formData.date}
                </span>
                <span className="info-badge keyboard-hint">
                  <FaArrowUp />/<FaArrowDown /> Arrow Keys • Enter = Next
                </span>
              </div>
            </div>

            <div className="header-actions">
              <button
                className="btn btn-secondary"
                onClick={() =>
                  navigate("/test-records", {
                    state: { className: formData.class },
                  })
                }
              >
                <FaTimes /> Back to Records
              </button>
            </div>
          </div>

          {/* Test Details Form - NON-EDITABLE */}
          <div className="test-details-form">
            <h3 className="form-section-title">Test Details</h3>
            <div className="form-grid read-only">
              <div className="form-group">
                <label className="form-label">
                  <span className="label-icon">#</span>
                  Test Number
                </label>
                <input
                  type="text"
                  value={formData.testNo}
                  readOnly
                  className="form-input read-only"
                />
              </div>

              <div className="form-group">
                <label className="form-label">
                  <span className="label-icon">
                    <FaSignature />
                  </span>
                  Test Name
                </label>
                <input
                  type="text"
                  value={formData.testName}
                  readOnly
                  className="form-input read-only"
                />
              </div>

              <div className="form-group">
                <label className="form-label">
                  <span className="label-icon">
                    <FaIdCard />
                  </span>
                  Test ID
                </label>
                <input
                  type="text"
                  value={formData.testId}
                  readOnly
                  className="form-input read-only"
                />
              </div>

              <div className="form-group">
                <label className="form-label">
                  <span className="label-icon">
                    <FaBook />
                  </span>
                  Subject
                </label>
                <input
                  type="text"
                  value={formData.subject}
                  readOnly
                  className="form-input read-only"
                />
              </div>

              <div className="form-group">
                <label className="form-label">
                  <span className="label-icon">🏫</span>
                  Class
                </label>
                <input
                  type="text"
                  value={formData.class}
                  readOnly
                  className="form-input read-only"
                />
              </div>

              <div className="form-group">
                <label className="form-label">
                  <span className="label-icon">👥</span>
                  Section
                </label>
                <input
                  type="text"
                  value={formData.section}
                  readOnly
                  className="form-input read-only"
                />
              </div>

              <div className="form-group">
                <label className="form-label">
                  <span className="label-icon">📊</span>
                  Total Marks
                </label>
                <input
                  type="number"
                  value={formData.totalMarks}
                  readOnly
                  className="form-input read-only"
                />
              </div>

              <div className="form-group">
                <label className="form-label">
                  <span className="label-icon">
                    <FaCalendarAlt />
                  </span>
                  Date
                </label>
                <input
                  type="text"
                  value={formData.date}
                  readOnly
                  className="form-input read-only"
                />
              </div>

              <div className="form-group">
                <label className="form-label">
                  <span className="label-icon">📅</span>
                  Day
                </label>
                <input
                  type="text"
                  value={formData.testDay}
                  readOnly
                  className="form-input read-only"
                />
              </div>
            </div>
          </div>

          {/* Statistics */}
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-icon total">👥</div>
              <div className="stat-content">
                <div className="stat-value">{stats.totalStudents}</div>
                <div className="label">Total Students</div>
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-icon submitted">✓</div>
              <div className="stat-content">
                <div className="stat-value">{stats.submittedStudents}</div>
                <div className="label">Marks Submitted</div>
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-icon avg">📊</div>
              <div className="stat-content">
                <div className="stat-value">{stats.averageMarks}</div>
                <div className="label">Average Marks</div>
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-icon high">🏆</div>
              <div className="stat-content">
                <div className="stat-value">{stats.highestMarks}</div>
                <div className="label">Highest Marks</div>
              </div>
            </div>
          </div>

          {/* Controls */}
          <div className="controls-section">
            <div className="search-box">
              <FaSearch
                className="search-icon"
                style={{ marginLeft: "-1rem" }}
              />
              <input
                type="text"
                placeholder="Search students by name or ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
              {searchTerm && (
                <button
                  className="clear-search"
                  onClick={() => setSearchTerm("")}
                >
                  ✕
                </button>
              )}
            </div>

            <div className="bulk-actions">
              <span className="bulk-label">Quick Fill:</span>
              <input
                type="number"
                placeholder="Enter marks"
                className="bulk-input"
                onKeyPress={(e) => {
                  if (e.key === "Enter") {
                    const marks = parseFloat(e.target.value);
                    if (!isNaN(marks)) {
                      handleBulkMarksUpdate(marks);
                      e.target.value = "";
                      if (inputRefs.current[0]) {
                        inputRefs.current[0].focus();
                        inputRefs.current[0].select();
                      }
                    }
                  }
                }}
              />
              <span className="keyboard-shortcut">Enter to apply</span>
            </div>

            <div className="action-buttons">
              <button className="btn btn-warning" onClick={handleSendData}>
                <FaPrint /> Print Preview
              </button>
              <button
                className="btn btn-success"
                onClick={handleUpdateTest}
                disabled={isUpdating || students.length === 0}
              >
                {isUpdating ? (
                  <>
                    <FaSpinner className="spin" />
                    Updating...
                  </>
                ) : (
                  <>
                    <FaUpload /> Update Test
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Table */}
          <div className="table-container">
            <div className="table-wrapper">
              <table className="marks-table">
                <thead>
                  <tr>
                    <th className="sticky-col">ID</th>
                    <th className="sticky-col">Student Name</th>
                    <th>Father's Name</th>
                    <th>Section</th>
                    <th>Total Marks</th>
                    <th className="marks-col">Obtained Marks</th>
                    <th>Percentage</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredStudents.length === 0 ? (
                    <tr>
                      <td colSpan="8" className="empty-cell">
                        <div className="empty-state">
                          <FaExclamationTriangle />
                          <div>
                            <p>No students found.</p>
                          </div>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    filteredStudents.map((student, index) => (
                      <tr
                        key={student.id}
                        className={`${student.status} ${
                          currentFocusIndex === index ? "row-focused" : ""
                        }`}
                        onClick={() => {
                          if (inputRefs.current[index]) {
                            inputRefs.current[index].focus();
                            inputRefs.current[index].select();
                          }
                        }}
                      >
                        <td className="sticky-col student-id">{student.id}</td>
                        <td className="sticky-col student-name">
                          {student.name}
                        </td>
                        <td className="father-name">{student.father_name}</td>
                        <td>{student.section}</td>
                        <td className="total-marks">{student.totalMarks}</td>
                        <td className="marks-input-cell">
                          <div className="input-wrapper">
                            <input
                              ref={(el) => (inputRefs.current[index] = el)}
                              type="number"
                              value={student.obtainedMarks || ""}
                              onChange={(e) =>
                                handleMarksUpdate(e, student.id, index)
                              }
                              className="marks-input"
                              max={student.totalMarks}
                              min="0"
                              placeholder="Enter marks"
                              step="0.5"
                              onFocus={() => setCurrentFocusIndex(index)}
                              onBlur={() => setCurrentFocusIndex(-1)}
                              onKeyDown={(e) => {
                                if (
                                  ["ArrowUp", "ArrowDown", "Enter"].includes(
                                    e.key
                                  )
                                ) {
                                  e.preventDefault();
                                }
                              }}
                            />
                            <div className="keyboard-hint-small">↑↓ Enter</div>
                          </div>
                        </td>
                        <td className="percentage-cell">
                          <span
                            className={`percentage-badge ${
                              student.percentage >= 50 ? "good" : "poor"
                            }`}
                          >
                            {student.percentage}%
                          </span>
                        </td>
                        <td>
                          <span className={`status-badge ${student.status}`}>
                            {student.status === "submitted"
                              ? "✓ Submitted"
                              : "⏳ Pending"}
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Footer Summary */}
          <div className="footer-summary">
            <div className="summary-content">
              <div className="progress-summary">
                <div className="progress-bar">
                  <div
                    className="progress-fill"
                    style={{ width: `${stats.completionRate}%` }}
                  ></div>
                </div>
                <div className="progress-text">
                  {stats.completionRate}% Complete ({stats.submittedStudents}/
                  {stats.totalStudents})
                </div>
                <div className="keyboard-tips">
                  <span className="tip">
                    <FaArrowUp /> Arrow Up = Previous
                  </span>
                  <span className="tip">
                    <FaArrowDown /> Arrow Down = Next
                  </span>
                  <span className="tip">Enter = Next Row</span>
                </div>
              </div>
              <div className="summary-actions">
                <button
                  className="btn btn-outline"
                  onClick={() => {
                    if (inputRefs.current[0]) {
                      inputRefs.current[0].focus();
                      inputRefs.current[0].select();
                    }
                  }}
                >
                  Focus First Input
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handleUpdateTest}
                  disabled={isUpdating || students.length === 0}
                >
                  {isUpdating ? (
                    <>
                      <FaSpinner className="spin" /> Updating...
                    </>
                  ) : (
                    <>
                      <FaUpload /> Final Update
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default UpdateTestSheet;
